import {
    Accordion,
    AccordionSummary,
    Typography,
    AccordionDetails,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    alpha,
    LinearProgress,
} from '@mui/material'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DragPreviewImage, useDrag } from 'react-dnd'
import { ReactElement, useState } from 'react'
import {
    floorLayoutDropTargets,
    TemplatePermissionMode,
    URLSearchParamsKeys,
    UserPermissions,
} from '../../../../constants/constants'
import { useAppSelector } from '../../../../redux/hook'
import theme from '../../../../theme/theme'
import { getAttributeById } from '../../../../utils/AttributeUtils'
import { IAsset } from '../../../../CustomHooks/FacilityMonitoring/getAssetsAndSensorsInBuilding'
import TruncatedText from '../../../Common/TruncatedText'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export interface IFreeAssetsTable {
    assetsType: string
    allAssetSensors: IAsset[]
    isLoading: boolean
}
export const AssetSensorTable = (props: IFreeAssetsTable): ReactElement => {
    const { t } = useTranslation()
    const { assetsType, allAssetSensors, isLoading } = props
    const [isExpanded, setIsExpanded] = useState<boolean>(true)
    const orgSlice = useAppSelector((state) => state.org)
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const { permissions } = orgSlice
    const { siteList } = fmSlice
    const hasWritePermission = permissions.includes(
        UserPermissions.ENTITY_WRITER,
    )
    const [searchParams] = useSearchParams()

    const selectedSiteUID = searchParams.get(
        URLSearchParamsKeys.selectedSiteUID,
    )
    const selectedSite = siteList.find(
        (site) => site.siteName === selectedSiteUID,
    )
    const canEdit =
        hasWritePermission &&
        selectedSite?.permissionLevel !== TemplatePermissionMode.READ

    return (
        <Accordion
            sx={{ boxShadow: 'none' }}
            expanded={isExpanded}
            onChange={() => setIsExpanded((isExpanded) => !isExpanded)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                        color: '#000',
                        fontSize: '20px',
                    }}
                >
                    {assetsType}
                </Typography>
            </AccordionSummary>
            {isLoading ? (
                <LinearProgress />
            ) : (
                <AccordionDetails>
                    {allAssetSensors && allAssetSensors.length > 0 ? (
                        <TableContainer component={Paper}>
                            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                                <TableHead
                                    sx={{
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.21,
                                        ),
                                    }}
                                >
                                    <TableRow>
                                        <TableCell>{t('assetName')}</TableCell>
                                        <TableCell>{t('deviceId')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {allAssetSensors.map((asset) => (
                                        <Row
                                            key={asset.uid}
                                            asset={asset}
                                            canEdit={canEdit}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography
                            component={'span'}
                            variant="body1"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center',
                                fontStyle: 'italic',
                                marginTop: '50px',
                            }}
                        >
                            {t('noFreeAssets')}
                        </Typography>
                    )}
                </AccordionDetails>
            )}
        </Accordion>
    )
}

interface IRow {
    asset: IAsset
    canEdit: boolean
}
const Row = (props: IRow) => {
    const { asset, canEdit } = props
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const {
        assetXCoordinateAttributeId,
        assetYCoordinateAttributeId,
        presentConfiguringAssetSensorMarkers,
    } = fmSlice
    // const handleDragStart = (item: any) => {
    //     // Optional: You can add logic here to handle drag start events
    // }
    const x =
        Number(
            getAttributeById(asset.attributes, assetXCoordinateAttributeId)
                ?.value,
        ) || 0
    const y =
        Number(
            getAttributeById(asset.attributes, assetYCoordinateAttributeId)
                ?.value,
        ) || 0
    const sensor = asset.relatedSensor
    const isPrevConfigured = !!sensor && (!!x || !!y)
    const isPresentConfiguring =
        presentConfiguringAssetSensorMarkers.findIndex(
            (marker) => marker.assetInstanceId === asset.entityInstanceId,
        ) !== -1

    const [, drag, preview] = useDrag({
        type: floorLayoutDropTargets.ASSET, // Define the drag type
        item: { ...asset },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        // end: (dropResult) => {
        //     // Handle drop result (optional)
        // },
        canDrag: () => canEdit && !isPrevConfigured && !isPresentConfiguring,
    })

    return (
        <>
            <DragPreviewImage
                connect={preview}
                src={'/assets/images/map/circle-with-theme-color.svg'}
            />
            <TableRow
                ref={(node) => {
                    if (node) drag(node)
                }}
                // onDragStart={handleDragStart}
                sx={{
                    cursor: canEdit
                        ? isPrevConfigured || isPresentConfiguring
                            ? 'not-allowed'
                            : 'grab'
                        : 'text',
                    backgroundColor: isPrevConfigured
                        ? alpha(theme.palette.primary.main, 0.1)
                        : isPresentConfiguring
                          ? alpha(theme.palette.warning.main, 0.1)
                          : '#FFFFFF',
                }}
            >
                <TableCell>
                    <TruncatedText text={asset.uid || ''} />
                </TableCell>
                <TableCell>
                    <TruncatedText text={sensor?.uid || ''} />
                </TableCell>
            </TableRow>
        </>
    )
}
